<template>
  <div>
    <v-row>
      <v-col>
        <span class="text--small text--bold">{{ $t('project.signing-checklist.dialogs.SigningChecklistStartSigningDialogSettingsStep.title') }}</span>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <app-alert-in-page variant="info">
          <p>{{ endSessionAlertText }}</p>
        </app-alert-in-page>
      </v-col>
    </v-row>
    <v-row v-if="isStartDateDisplayed">
      <v-col>
        <div class="font-weight-medium mb-2">{{ $t('project.signing-checklist.dialogs.SigningChecklistStartSigningDialogSettingsStep.startDateLabel') }}</div>
        <v-row>
          <v-col>
            <AppTextField v-model="startDate.date.$model"
                          type="date"
                          :disabled="currentRoom.inClosing"
                          :min="minStartDate"
                          :error="startDateErrors.length > 0"
                          clearable
                          @clear="$emit('reset-validation', 'startDate')"
            />
            <template v-if="startDateErrors.length">
              <p class="text-error mt-1 text--x-small">
                {{ startDateErrors[0] }}
              </p>
            </template>
          </v-col>
          <v-col>
            <AppTextField v-model="startDate.time.$model"
                          :disabled="currentRoom.inClosing"
                          type="time"
                          :error="startDateTimeErrors.length > 0"
                          clearable
                          @clear="$emit('reset-validation', 'startDate')"
            />
            <template v-if="startDateTimeErrors.length">
              <p class="text-error mt-1 text--x-small">
                {{ startDateTimeErrors[0] }}
              </p>
            </template>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <div class="font-weight-medium mb-2">{{ endLabel }}</div>
        <v-row>
          <v-col>
            <AppTextField v-model="endDate.date.$model"
                          type="date"
                          :min="minEndDate"
                          :error="endDateErrors.length > 0"
                          clearable
                          @clear="$emit('reset-validation', 'endDate')"
            />
            <template v-if="endDateErrors.length">
              <p class="text-error mt-1 text--x-small">
                {{ endDateErrors[0] }}
              </p>
            </template>
          </v-col>
          <v-col>
            <AppTextField v-model="endDate.time.$model"
                          type="time"
                          :error="endDateTimeErrors.length > 0"
                          clearable
                          @clear="$emit('reset-validation', 'endDate')"
            />
            <template v-if="endDateTimeErrors.length">
              <p class="text-error mt-1 text--x-small">
                {{ endDateTimeErrors[0] }}
              </p>
            </template>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row v-if="!isEditMode"
           class="mt-10"
    >
      <v-col>
        <div class="d-flex align-center font-weight-medium mb-2">
          {{ $t('project.signing-checklist.dialogs.SigningChecklistStartSigningDialogSettingsStep.customMessageLabel') }}
          <AppHelpTooltip :text="$t('project.signing-checklist.dialogs.SigningChecklistStartSigningDialogSettingsStep.customMessageTooltip')" />
        </div>
        <VisualTextEditor :value="customMessage"
                          :placeholder="$t('project.signing-checklist.dialogs.SigningChecklistStartSigningDialogSettingsStep.customMessagePlaceholder')"
                          counter="footer"
                          :error-messages="customMessageErrors"
                          :max-length="customMessageMaxCharacters"
                          @input="$emit('update:customMessage', $event)"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import dayOfYear from 'dayjs/plugin/dayOfYear'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { mapState } from 'vuex'

import AppHelpTooltip from '@/common/AppHelpTooltip.vue'
import AppTextField from '@/common/AppTextField.vue'
import { formFieldErrors } from '@/common/utils/form'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import { DATE_FORMAT, DATE_AND_TIME_FORMAT } from '@/project/signing-checklist/list/dateAndTimeFormat'
import { START_SIGNING_ACTIONS } from '@/project/signing-checklist/list/startSigningActions'

dayjs.extend(dayOfYear)
dayjs.extend(isSameOrBefore)

export default {
  name: 'SigningChecklistStartSigningDialogSettingsStep',
  components: { AppHelpTooltip, AppTextField, VisualTextEditor },
  props: {
    action: {
      type: String,
      required: true,
    },
    startDate: {
      type: Object,
      required: true,
    },
    endDate: {
      type: Object,
      required: true,
    },
    customMessage: {
      type: String,
      required: true,
    },
    customMessageErrors: {
      type: Array,
      required: true,
    },
    customMessageMaxCharacters: {
      type: Number,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      START_SIGNING_ACTIONS,
      minStartDate: null,
    }
  },
  computed: {
    ...mapState('room', ['currentRoom']),
    computedCompleteStartDate () {
      if (this.startDate.date.$model && this.startDate.time.$model) {
        return dayjs(`${this.startDate.date.$model} ${this.startDate.time.$model}`, DATE_AND_TIME_FORMAT).format()
      }
      return null
    },
    computedCompleteEndDate () {
      if (this.endDate.date.$model && this.endDate.time.$model) {
        return dayjs(`${this.endDate.date.$model} ${this.endDate.time.$model}`, DATE_AND_TIME_FORMAT).format()
      }
      return null
    },
    endDateErrors () {
      return formFieldErrors(
        this.endDate.date,
        [
          ['required', this.$t('project.signing-checklist.dialogs.SigningChecklistStartSigningDialogSettingsStep.errors.endDateRequired')],
          ['afterCurrentDate', this.$t('project.signing-checklist.dialogs.SigningChecklistStartSigningDialogSettingsStep.errors.dateTooOld')],
          ['afterStartDate', this.$t('project.signing-checklist.dialogs.SigningChecklistStartSigningDialogSettingsStep.errors.endDateBeforeStartDate')],
        ],
      )
    },
    endDateTimeErrors () {
      return formFieldErrors(
        this.endDate.time,
        [
          ['required', this.$t('project.signing-checklist.dialogs.SigningChecklistStartSigningDialogSettingsStep.errors.endTimeRequired')],
        ],
      )
    },
    endLabel () {
      if (this.action === START_SIGNING_ACTIONS.NOW) {
        return this.$t('project.signing-checklist.dialogs.SigningChecklistStartSigningDialogSettingsStep.endDateOnlyLabel')
      }
      return this.$t('project.signing-checklist.dialogs.SigningChecklistStartSigningDialogSettingsStep.endDateLabel')
    },
    endSessionAlertText () {
      return this.action === START_SIGNING_ACTIONS.EDIT && this.currentRoom.inClosing ? this.$t('project.signing-checklist.dialogs.SigningChecklistStartSigningDialogSettingsStep.endSessionEditAlertText') : this.$t('project.signing-checklist.dialogs.SigningChecklistStartSigningDialogSettingsStep.endSessionNewAlertText')
    },
    isStartDateDisplayed () {
      return (this.action === START_SIGNING_ACTIONS.SCHEDULED) || this.isEditMode
    },
    minEndDate () {
      if (this.computedCompleteStartDate) {
        return dayjs(this.computedCompleteStartDate).format(DATE_FORMAT)
      }
      return dayjs().format(DATE_FORMAT)
    },
    startDateErrors () {
      return formFieldErrors(
        this.startDate.date,
        [
          ['required', this.$t('project.signing-checklist.dialogs.SigningChecklistStartSigningDialogSettingsStep.errors.startDateRequired')],
          ['afterCurrentDate', this.$t('project.signing-checklist.dialogs.SigningChecklistStartSigningDialogSettingsStep.errors.dateTooOld')],
        ],
      )
    },
    startDateTimeErrors () {
      return formFieldErrors(
        this.startDate.time,
        [
          ['required', this.$t('project.signing-checklist.dialogs.SigningChecklistStartSigningDialogSettingsStep.errors.startTimeRequired')],
        ],
      )
    },
  },
  created () {
    this.minStartDate = dayjs().format(DATE_FORMAT)
  },
  methods: {
    dayjs,
  },
}
</script>

<style scoped lang="scss">
:deep(.v-text-field .v-input__icon--clear) {
  opacity: 1;
}
</style>
